import { Injectable } from '@angular/core';
import { VersionUpdateService } from './version-update.service';
import semverCompare from 'semver-compare';

interface VersionMetadata {
  version: string;
  versionUpdateEnabled?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class VersionPollingService {
  private currentVersion = '';
  private pollInterval: number | null = null;
  private lastCheckTime = 0;
  private static readonly CHECK_INTERVAL = 7200000; // 2 hours
  private static readonly SERVICE_TIMEOUT = 10000;
  private isPolling = false;
  private appName = 'Unknown';

  private readonly logger = {
    log: (message: string, data?: unknown) => {
      console.log(`[${this.appName}VersionPolling] ${message}`, data || '');
    },
    warn: (message: string, data?: unknown) => {
      console.warn(`[${this.appName}VersionPolling] ${message}`, data || '');
    },
    error: (message: string, data?: unknown) => {
      console.error(`[${this.appName}VersionPolling] ${message}`, data || '');
    }
  };

  constructor(private readonly versionUpdateService: VersionUpdateService) {}

  startPolling(currentVersion: string, appName = 'App'): void {
    if (this.isPolling) {
      this.logger.warn('Already polling, skipping start');
      return;
    }

    this.currentVersion = currentVersion;
    this.appName = appName;
    this.isPolling = true;

    this.waitForAppReady().then(() => {
      this.checkForUpdates();
    });

    this.startInterval();

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopInterval();
      } else {
        this.startInterval();

        const timeSinceLastCheck = Date.now() - this.lastCheckTime;
        if (timeSinceLastCheck >= VersionPollingService.CHECK_INTERVAL) {
          this.checkForUpdates();
        }
      }
    });
  }

  stopPolling(): void {
    this.isPolling = false;
    this.stopInterval();
  }

  private waitForAppReady(): Promise<void> {
    return new Promise((resolve) => {
      if (document.readyState === 'complete') {
        setTimeout(resolve, 2000);
      } else {
        window.addEventListener('load', () => {
          setTimeout(resolve, 2000);
        });
      }
    });
  }

  private startInterval(): void {
    if (!this.pollInterval) {
      this.pollInterval = window.setInterval(() => {
        if (!document.hidden) {
          this.checkForUpdates();
        }
      }, VersionPollingService.CHECK_INTERVAL);
    }
  }

  private stopInterval(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  private async checkForUpdates(): Promise<void> {
    this.lastCheckTime = Date.now();

    try {
      const url = `/assets/version.json?ts=${Date.now()}`;

      const response = await fetch(url, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache'
        },
        signal: AbortSignal.timeout(VersionPollingService.SERVICE_TIMEOUT)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: VersionMetadata = await response.json();

      const deployedVersion = data.version;

      if (deployedVersion !== this.currentVersion) {
        const isRollback = this.isVersionRollback(
          this.currentVersion,
          deployedVersion
        );

        this.handleUpdatePrompt(data, isRollback);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        this.logger.warn(
          `Version check timed out after ${VersionPollingService.SERVICE_TIMEOUT} milliseconds, will retry on next poll`
        );
      } else {
        this.logger.error('Failed to check for updates:', error);
      }
    }
  }

  private handleUpdatePrompt(
    metadata: VersionMetadata,
    isRollback = false
  ): void {
    if (metadata.versionUpdateEnabled === false) {
      return;
    }
    const newVersion = metadata.version;

    this.logger.log('Version update available', {
      current: this.currentVersion,
      new: newVersion,
      isRollback
    });

    this.versionUpdateService.showUpdatePrompt({
      currentVersion: this.currentVersion,
      newVersion,
      isRollback
    });
  }

  private isVersionRollback(
    currentVersion: string,
    deployedVersion: string
  ): boolean {
    try {
      const comparison = semverCompare(currentVersion, deployedVersion);

      return comparison > 0;
    } catch (error) {
      this.logger.warn(
        'Failed to compare versions using semver-compare, falling back to string comparison',
        {
          current: currentVersion,
          deployed: deployedVersion,
          error
        }
      );

      return deployedVersion < currentVersion;
    }
  }
}
